import * as React from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Box,
	Chip,
	TablePagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { currencyFormatter } from 'utilities/currencyFormatter';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setTenantOccupancy } from 'state';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	'&.MuiTableCell-head': {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.common.white,
		fontWeight: 'bold',
	},
	'&.MuiTableCell-body': {
		fontSize: 14,
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:nth-of-type(odd)': {
		backgroundColor: theme.palette.action.hover,
	},
	'&:hover': {
		backgroundColor: theme.palette.action.selected,
	},
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

const getArrearsSeverity = (days) => {
	if (days <= 7) return { color: 'warning', label: 'Low' };
	if (days <= 30) return { color: 'error', label: 'Medium' };
	return { color: 'error', label: 'High' };
};

export default function ArrearsTable({ rows, summary }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleNavigateTenantPayments = (e, row) => {
		e.stopPropagation();
		console.log(row);
		if (row.occupancy_details || row.occupancy_id) {
			const occupancy = row.occupancy_details || { id: row.occupancy_id };
			dispatch(setTenantOccupancy(occupancy));
			navigate("/tenant-payments");
		}
	};

	return (
		<Box>
			{/* Summary Cards */}
			<Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="error" fontWeight="bold">
						{currencyFormatter(summary?.total_amount_in_arrears || 0)}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Amount in Arrears
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="primary" fontWeight="bold">
						{summary?.tenants_in_arrears || 0}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Tenants in Arrears
					</Typography>
				</Paper>
				<Paper sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
					<Typography variant="h6" color="secondary" fontWeight="bold">
						{summary?.total_records || 0}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total Records
					</Typography>
				</Paper>
			</Box>

			{/* Table */}
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 700 }} aria-label="arrears table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Tenant Name</StyledTableCell>
							<StyledTableCell>Unit Name</StyledTableCell>
							<StyledTableCell>Property Name</StyledTableCell>
							<StyledTableCell>Days in Arrears</StyledTableCell>
							<StyledTableCell>Rent Amount</StyledTableCell>
							<StyledTableCell>Paid Amount</StyledTableCell>
							<StyledTableCell>Amount in Arrears</StyledTableCell>
							<StyledTableCell>Period</StyledTableCell>
							<StyledTableCell>Status</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length > 0 ? (
							rows
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, index) => {
									const severity = getArrearsSeverity(row.days_in_arrears);
									return (
										<StyledTableRow key={index}>
											<TableCell>
												{row.occupancy_details || row.occupancy_id ? (
													<Typography
														component="span"
														onClick={(e) => handleNavigateTenantPayments(e, row)}
														sx={{
															cursor: 'pointer',
															color: '#1976d2',
															textDecoration: 'underline',
															display: 'inline-block',
															'&:hover': {
																color: '#1565c0',
																textDecoration: 'underline',
															},
														}}
													>
														{row.tenant_name}
													</Typography>
												) : (
													<Typography component="span">{row.tenant_name}</Typography>
												)}
											</TableCell>
											<TableCell>{row.unit_name}</TableCell>
											<TableCell>{row.property_name}</TableCell>
											<TableCell>
												<Chip
													label={`${row.days_in_arrears} days`}
													color={severity.color}
													size="small"
												/>
											</TableCell>
											<TableCell>{currencyFormatter(row.rent_amount)}</TableCell>
											<TableCell>{currencyFormatter(row.paid_amount)}</TableCell>
											<TableCell>
												<Typography color="error" fontWeight="bold">
													{currencyFormatter(row.amount_in_arrears)}
												</Typography>
											</TableCell>
											<TableCell>
												{new Date(row.period_start_date).toLocaleDateString()} - {new Date(row.period_end_date).toLocaleDateString()}
											</TableCell>
											<TableCell>
												<Chip
													label={row.is_current_period ? 'Current' : 'Previous'}
													color={row.is_current_period ? 'primary' : 'default'}
													size="small"
												/>
											</TableCell>
										</StyledTableRow>
									);
								})
						) : (
							<TableRow>
								<TableCell colSpan={9} align="center">
									<Typography variant="body1" color="text.secondary">
										No arrears records found
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={rows.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>
		</Box>
	);
} 